import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Select } from '@customerio/pluma-components/react';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { getSubscriptionTopicsQueryOptions } from '@/services/db/subscription-topic/queries';

/** The topic filter's "no topic assigned" option, which the list endpoints read as topic 0. */
const NO_TOPIC = '0';

type TopicFilterProps = {
  /** The selected topic id, `0` for "None", or null for no filter. */
  selectedTopicId: string | null;
  onTopicChange: (topicId: string | null) => void;
};

/**
 * The workspace's subscription topics. Not the suspense read the entity exports: a filter bar
 * waiting on its catalog would take the whole list down to a loader with it.
 */
function useTopicCatalog() {
  return useQuery(getSubscriptionTopicsQueryOptions(requireWorkspaceId(), useQueryClient()));
}

/** Whether the workspace has subscription topics, which is when a topic filter is offered. */
export function useHasSubscriptionTopics(): boolean {
  return (useTopicCatalog().data?.length ?? 0) > 0;
}

/**
 * Filters a list by subscription topic, including "None" for items with no topic. Picking the
 * selected option again clears the filter.
 */
export function TopicFilter({ selectedTopicId, onTopicChange }: TopicFilterProps) {
  const topicsQuery = useTopicCatalog();
  const options = topicsQuery.isPending
    ? []
    : [
        { label: 'None', value: NO_TOPIC },
        ...(topicsQuery.data ?? []).map((topic) => ({ label: topic.name, value: topic.id })),
      ];

  return (
    <Select
      size="sm"
      options={options}
      placeholder="Select a topic"
      ariaLabel="Topic"
      isDisabled={topicsQuery.isPending}
      value={selectedTopicId ?? undefined}
      onChange={(value: string | null) => onTopicChange(value == null || value === selectedTopicId ? null : value)}
    />
  );
}
